import type { VRLocomotionMode, VRPerformanceSnapshot, VRQualityProfile, VRViewerKind } from "./vr.types";

const MAX_SAMPLES = 90;
const WARNING_THRESHOLD_MS = 16.7;
const CRITICAL_THRESHOLD_MS = 25;
const DEGRADE_WINDOW = 45;
const POST_DEGRADE_FALLBACK_COOLDOWN = 90;
const FALLBACK_WINDOW = 210;

export class VRPerformanceMonitor {
  private samples: number[] = [];
  private qualityProfile: VRQualityProfile = "vr-safe";
  private viewerKind: VRViewerKind = null;
  private locomotionMode: VRLocomotionMode = "stationary";
  private warningStreak = 0;
  private criticalStreak = 0;
  private fallbackCooldownRemaining = 0;

  public reset() {
    this.samples = [];
    this.qualityProfile = "vr-safe";
    this.viewerKind = null;
    this.locomotionMode = "stationary";
    this.warningStreak = 0;
    this.criticalStreak = 0;
    this.fallbackCooldownRemaining = 0;
  }

  public getQualityProfile() {
    return this.qualityProfile;
  }

  public sample(frameMs: number, viewerKind: VRViewerKind, locomotionMode: VRLocomotionMode): VRPerformanceSnapshot {
    if (Number.isFinite(frameMs) && frameMs > 0) {
      this.samples.push(frameMs);
      if (this.samples.length > MAX_SAMPLES) {
        this.samples.shift();
      }
    }

    this.viewerKind = viewerKind;
    this.locomotionMode = locomotionMode;

    if (frameMs >= WARNING_THRESHOLD_MS) {
      this.warningStreak += 1;
    } else {
      this.warningStreak = 0;
    }

    const averageFrameMs = this.samples.length > 0
      ? this.samples.reduce((sum, value) => sum + value, 0) / this.samples.length
      : 0;
    const averageFps = averageFrameMs > 0 ? 1000 / averageFrameMs : 0;

    const shouldDegradeQuality = this.qualityProfile === "vr-safe" &&
      this.warningStreak >= DEGRADE_WINDOW;

    if (shouldDegradeQuality) {
      this.qualityProfile = "vr-degraded";
      this.criticalStreak = 0;
      this.fallbackCooldownRemaining = POST_DEGRADE_FALLBACK_COOLDOWN;
    }

    if (this.qualityProfile === "vr-degraded" && this.fallbackCooldownRemaining > 0) {
      this.fallbackCooldownRemaining -= 1;
      this.criticalStreak = 0;
    } else if (this.qualityProfile === "vr-degraded" && frameMs >= CRITICAL_THRESHOLD_MS) {
      this.criticalStreak += 1;
    } else if (frameMs < CRITICAL_THRESHOLD_MS) {
      this.criticalStreak = 0;
    }

    const shouldFallbackToDesktop = this.qualityProfile === "vr-degraded" &&
      this.criticalStreak >= FALLBACK_WINDOW;

    return {
      averageFrameMs,
      averageFps,
      qualityProfile: this.qualityProfile,
      shouldDegradeQuality,
      shouldFallbackToDesktop,
    };
  }

  public getDebugState() {
    return {
      sampleCount: this.samples.length,
      qualityProfile: this.qualityProfile,
      viewerKind: this.viewerKind,
      locomotionMode: this.locomotionMode,
      warningStreak: this.warningStreak,
      criticalStreak: this.criticalStreak,
      fallbackCooldownRemaining: this.fallbackCooldownRemaining,
    };
  }
}
